import DOMPurify from "dompurify";

/**
 * Sanitizes HTML to prevent XSS attacks
 * @param {string} html - Raw HTML string
 * @param {Object} options - DOMPurify config options
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (html, options = {}) => {
  if (!html) return "";

  const defaultConfig = {
    ALLOWED_TAGS: [
      "p",
      "br",
      "b",
      "i",
      "em",
      "strong",
      "a",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "div",
      "span",
      "table",
      "thead",
      "tbody",
      "tr",
      "td",
      "th",
      "img",
      "sup",
      "sub",
      "blockquote",
      "pre",
      "code",
      "hr",
      "caption",
      "col",
      "colgroup",
      "html",
      "head",
      "body",
      "header",
      "meta",
      "style",
      "u",
      "svg",
      "image",
      "g",
      "rect",
      "circle",
      "ellipse",
      "line",
      "path",
      "polygon",
      "polyline",
      "text",
      "tspan",
      "defs",
      "clipPath",
      "mask"
    ],
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "class",
      "id",
      "style",
      "target",
      "title",
      "width",
      "height",
      "border",
      "cellspacing",
      "cellpadding",
      "colspan",
      "rowspan",
      "valign",
      "align",
      "nowrap",
      "type",
      "http-equiv",
      "content",
      "scrolling",
      "loading",
      "crossorigin",
      "sizes",
      "srcset",
      "rel",
      "xlink:href",
      "preserveAspectRatio",
      "viewBox",
      "xmlns",
      "xmlns:xlink",
      "x",
      "y",
      "dx",
      "dy",
      "x1",
      "x2",
      "y1",
      "y2",
      "cx",
      "cy",
      "r",
      "rx",
      "ry",
      "d",
      "fill",
      "stroke",
      "stroke-width",
      "stroke-linecap",
      "stroke-linejoin",
      "opacity",
      "fill-opacity",
      "stroke-opacity",
      "transform",
      "points"
    ],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
    // Allow data URIs for inline images (base64 encoded)
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
    // Allow SVG namespace attributes
    ADD_ATTR: ["xmlns", "xmlns:xlink"],
    // Preserve whitespace
    FORCE_BODY: false,
    // Don't return just body content
    WHOLE_DOCUMENT: false,
    // Return full document structure
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false
  };

  return DOMPurify.sanitize(html, { ...defaultConfig, ...options });
};

/**
 * Creates props for dangerouslySetInnerHTML with sanitized HTML
 * @param {string} html - Raw HTML string
 * @param {Object} options - DOMPurify config options
 * @returns {Object} Props object for dangerouslySetInnerHTML
 */
export const createSafeHtml = (html, options = {}) => ({
  dangerouslySetInnerHTML: { __html: sanitizeHtml(html, options) }
});
